import { SchemaAction } from '@rossum/api-client/schemas';
import {
  Autocomplete,
  ListItem,
  ListItemText,
  ListSubheader,
  SvgIcon,
  TextField,
} from '@rossum/ui/material';
import { Fragment, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { knownActionGroups } from '../actions';
import { ActionIcon } from './ActionIcon';

type ActionAutocompleteProps = {
  value: SchemaAction | undefined;
  onChange: (s: SchemaAction | undefined) => void;
  options: {
    name: string;
    description: ReactNode;
    descriptionShort: ReactNode;
    serialized: SchemaAction;
    groupIntlKey: string;
    icon: typeof SvgIcon;
    resolveAction: (a: SchemaAction) => boolean;
  }[];
};

export const ActionAutocomplete = ({
  value,
  onChange,
  options,
}: ActionAutocompleteProps) => {
  const intl = useIntl();

  const stableValue = useMemo(
    () =>
      value
        ? (options.find(action => action.resolveAction(value)) ?? null)
        : null,
    [options, value]
  );

  return (
    <Autocomplete
      value={stableValue}
      onChange={(_, newValue) =>
        onChange(
          newValue
            ? {
                ...newValue.serialized,
                payload:
                  // It's better to always send payload to B/E (even empty) to get consistent error handling.
                  'payload' in newValue.serialized
                    ? newValue.serialized.payload
                    : {},
              }
            : undefined
        )
      }
      disablePortal
      getOptionLabel={opt => opt.name}
      options={options}
      renderOption={(
        { key, ...props },
        { name, descriptionShort, icon: Icon }
      ) => {
        return (
          <ListItem
            key={key}
            {...props}
            sx={{
              // Target the autocomplete option class directly for higher specificity
              '&.MuiAutocomplete-option': {
                padding: '0px 16px',
              },
            }}
          >
            <ActionIcon icon={Icon} withCustomPadding />
            <ListItemText primary={name} secondary={descriptionShort} />
          </ListItem>
        );
      }}
      groupBy={action => action.groupIntlKey}
      renderGroup={({ group, children, key }) => {
        const knownGroup = knownActionGroups.find(g => g === group) ?? 'other';
        return knownGroup ? (
          <Fragment key={key}>
            <ListSubheader
              disableSticky
              sx={{
                borderTop: theme => `1px solid ${theme.palette.divider}`,
                py: 1,
                '&:first-of-type': { borderTop: 0 },
                lineHeight: '20px',
              }}
            >
              {intl.formatMessage({
                id: `features.queueSettings.rules.actionGroup.${knownGroup}`,
              })}
            </ListSubheader>
            {children}
          </Fragment>
        ) : null;
      }}
      renderInput={params => (
        <TextField
          {...params}
          size="small"
          label={intl.formatMessage({
            id: `features.queueSettings.rules.action`,
          })}
        />
      )}
      disableClearable={value !== null}
    />
  );
};
